import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/Shared";
import ListView from "@/components/ListView";
import KanbanBoard, { ModeToggle } from "@/components/KanbanBoard";
import { formatDate } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import { Plus, Loader2 } from "lucide-react";
import { INDUSTRIES, EMPLOYEE_SIZES } from "@/lib/customerMeta";

const emptyForm = { name: "", company: "", phone: "", email: "", gstin: "",
  billing_address: "", city: "", state: "", industry: "", employee_size: "" };

export default function Customers() {
  const [data, setData] = useState(null);
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(params.get("new") === "1");
  useEffect(() => { if (params.get("new") === "1") { setOpen(true); setParams({}, { replace: true }); } }, [params, setParams]);
  useEffect(() => {
    const cloneId = params.get("clone");
    if (cloneId) {
      api.get(`/customers/${cloneId}`).then(({ data }) => {
        setForm({ name: `${data.name} (Copy)`, company: data.company || "", phone: "", email: data.email || "",
          gstin: "", billing_address: data.billing_address || "", city: data.city || "", state: data.state || "",
          industry: data.industry || "", employee_size: data.employee_size || "" });
        setOpen(true); setParams({}, { replace: true });
      }).catch(() => {});
    }
  }, [params, setParams]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [mode, setModeState] = useState(() => localStorage.getItem("tradedesk.mode.customers") || "list");
  const setMode = (m) => { setModeState(m); localStorage.setItem("tradedesk.mode.customers", m); };

  const load = async () => {
    setData((await api.get(`/customers?page_size=200`)).data);
  };
  useEffect(() => { load(); }, []);
  const rows = (data?.items || []).map((c) => ({ ...c, path: `/customers/${c.id}` }));
  const stateColumns = useMemo(() => [...new Set(rows.map((c) => c.state || "Unknown"))].sort().map((st) => ({ id: st, color: "#7F8DE1" })), [rows]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/customers", { ...form, shipping_address: form.billing_address });
      toast.success("Customer added"); setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Customers" subtitle="Matched by phone (last 10 digits)"
        actions={<>
          <ModeToggle mode={mode} setMode={setMode} testid="customers-mode-toggle" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="h-10 bg-[#0B7C5A] hover:bg-[#09664a]" data-testid="customer-add-btn"><Plus className="w-4 h-4" /> Add customer</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>New customer</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <F label="Name"><Input data-testid="customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
                <F label="Company"><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></F>
                <F label="Phone"><Input data-testid="customer-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></F>
                <F label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
                <F label="GSTIN"><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></F>
                <F label="City"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></F>
                <F label="State"><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></F>
                <F label="Industry"><Sel value={form.industry} onChange={(v) => setForm({ ...form, industry: v })} options={INDUSTRIES} testid="customer-industry" /></F>
                <F label="Employee size"><Sel value={form.employee_size} onChange={(v) => setForm({ ...form, employee_size: v })} options={EMPLOYEE_SIZES} testid="customer-employee-size" /></F>
                <div className="col-span-2"><F label="Billing address"><Input value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} /></F></div>
              </div>
              <DialogFooter>
                <Button onClick={save} disabled={saving || !form.name || form.phone.length < 10}
                  className="bg-[#101D51] hover:bg-[#080852]" data-testid="customer-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save customer"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>} />
      {mode === "board" ? <KanbanBoard testid="customers-kanban" rows={rows} sumKey={null} groupKey={(c) => c.state || "Unknown"} columns={stateColumns} title={(c) => c.company || c.name} subtitle={(c) => `+91 ${c.phone}${c.city ? " · " + c.city : ""}`} meta={(c) => c.gstin ? "GST" : "B2C"} /> : null}

      {mode === "list" && <ListView object="customers" title="Customers" rows={rows} loading={!data} me={null} statuses={[]} defaultSort={{ key: "created_at", dir: "desc" }} onDataChange={load}
        searchKeys={["name", "phone", "company", "gstin", "city"]}
        views={[
          { id: "all", name: "All Customers", filter: () => true },
          { id: "recent", name: "Recently Added (30d)", filter: (c) => c.created_at > new Date(Date.now() - 30 * 86400000).toISOString() },
          { id: "gst", name: "GST Registered", filter: (c) => !!c.gstin },
          { id: "b2c", name: "Individuals (no GSTIN)", filter: (c) => !c.gstin },
        ]}
        columns={[
          { key: "name", label: "Name" }, { key: "company", label: "Company", render: (c) => c.company || "—" },
          { key: "phone", label: "Phone", render: (c) => `+91 ${c.phone}` }, { key: "city", label: "City", render: (c) => c.city || "—" },
          { key: "state", label: "State", render: (c) => c.state || "—" }, { key: "gstin", label: "GSTIN", render: (c) => <span className="font-mono text-xs">{c.gstin || "—"}</span> },
          { key: "created_at", label: "Created", render: (c) => formatDate(c.created_at) },
          { key: "last_activity_at", label: "Last Activity", render: (c) => (c.last_activity_at ? formatDate(c.last_activity_at) : "—") },
        ]} />}
    </div>
  );
}
function F({ label, children }) {
  return <div><label className="text-xs font-medium text-[#6B7189]">{label}</label><div className="mt-1">{children}</div></div>;
}
function Sel({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="h-10 w-full rounded-md border border-[#E6E8F0] px-2 text-sm text-[#101D51] bg-white">
      <option value="">—</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>);
}
